import { useState, useCallback } from "react";
import { Search, Copy, Check, Ship, Building2, Hash, Anchor, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchTab() {
  const [query, setQuery] = useState("");
  const [searchType, setSearchType] = useState("all");
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(null);

  const handleSearch = useCallback(async () => {
    if (!query.trim()) {
      toast.error("Please enter a search query");
      return;
    }

    setIsLoading(true);
    setHasSearched(true);

    try {
      const response = await axios.get(`${API_URL}/search`, {
        params: {
          q: query.trim(),
          type: searchType,
        },
      });
      setResults(response.data);
      
      if (response.data.length === 0) {
        toast.info("No results found");
      } else {
        toast.success(`Found ${response.data.length} results`);
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Search failed. Please try again.");
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [query, searchType]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const copyEmail = async (email) => {
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      setCopiedEmail(email);
      toast.success("Email copied to clipboard");
      setTimeout(() => setCopiedEmail(null), 2000);
    } catch {
      toast.error("Failed to copy email");
    }
  };

  const copyAllEmails = async (result) => {
    const emails = [
      result.confirmed_email,
      result.confirmed_email_2,
      result.generated_email,
      result.email_deck_engine,
      result.email_prov_bond
    ].filter(e => e && e.trim());
    
    if (emails.length === 0) {
      toast.error("No emails to copy");
      return;
    }
    
    try {
      await navigator.clipboard.writeText(emails.join('\n'));
      toast.success(`Copied ${emails.length} email(s) to clipboard`);
    } catch {
      toast.error("Failed to copy emails");
    }
  };

  const getMatchScoreClass = (score) => {
    if (score >= 90) return "high";
    if (score >= 75) return "medium";
    return "low";
  };

  const getSearchTypeIcon = () => {
    switch (searchType) {
      case "vessel":
        return <Ship className="w-4 h-4" />;
      case "company":
        return <Building2 className="w-4 h-4" />;
      case "imo":
        return <Hash className="w-4 h-4" />;
      default:
        return <Search className="w-4 h-4" />;
    }
  };

  const getPrimaryEmail = (result) => {
    return result.confirmed_email || result.confirmed_email_2 || result.email_deck_engine || result.email_prov_bond || null;
  };

  const getEmailCount = (result) => {
    return [
      result.confirmed_email,
      result.confirmed_email_2,
      result.generated_email,
      result.email_deck_engine,
      result.email_prov_bond
    ].filter(e => e && e.trim()).length;
  };

  const EmailCell = ({ result }) => {
    const primaryEmail = getPrimaryEmail(result);
    const emailCount = getEmailCount(result);
    
    if (emailCount === 0) {
      return <span className="text-slate-400 italic">No email</span>;
    }

    return (
      <Popover>
        <PopoverTrigger asChild>
          <button className="text-left group">
            <span className="font-mono text-sm text-ocean-700 break-all hover:underline">
              {primaryEmail}
            </span>
            {emailCount > 1 && (
              <Badge variant="secondary" className="ml-2 bg-ocean-100 text-ocean-700 text-xs">
                +{emailCount - 1} more
              </Badge>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-96 p-4" align="start">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-heading font-semibold text-ocean-900">All Emails</h4>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyAllEmails(result)}
                className="h-7 text-xs"
              >
                <Copy className="w-3 h-3 mr-1" />
                Copy All
              </Button>
            </div>
            <div className="space-y-2">
              {result.confirmed_email && (
                <EmailRow label="Confirmed Email" email={result.confirmed_email} onCopy={copyEmail} copiedEmail={copiedEmail} />
              )}
              {result.confirmed_email_2 && (
                <EmailRow label="Confirmed Email 2" email={result.confirmed_email_2} onCopy={copyEmail} copiedEmail={copiedEmail} />
              )}
              {result.generated_email && (
                <EmailRow label="Generated Email" email={result.generated_email} onCopy={copyEmail} copiedEmail={copiedEmail} isMultiple />
              )}
              {result.email_deck_engine && (
                <EmailRow label="Deck / Engine" email={result.email_deck_engine} onCopy={copyEmail} copiedEmail={copiedEmail} />
              )}
              {result.email_prov_bond && (
                <EmailRow label="Prov / Bond" email={result.email_prov_bond} onCopy={copyEmail} copiedEmail={copiedEmail} />
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  const EmailRow = ({ label, email, onCopy, copiedEmail, isMultiple }) => (
    <div className="flex items-start justify-between gap-2 p-2 bg-slate-50 rounded-md">
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500 mb-0.5">{label}</p>
        <p className={`font-mono text-xs text-ocean-800 ${isMultiple ? 'whitespace-pre-wrap' : 'truncate'}`}>
          {email}
        </p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onCopy(email)}
        className="h-6 w-6 p-0 flex-shrink-0"
      >
        {copiedEmail === email ? (
          <Check className="w-3 h-3 text-green-600" />
        ) : (
          <Copy className="w-3 h-3 text-slate-400" />
        )}
      </Button>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Search Section */}
      <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-card">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading font-bold text-2xl text-ocean-900 mb-2 text-center">
            Search Vessel Database
          </h2>
          <p className="text-slate-500 text-center mb-6">
            Find vessel emails by name, company, or IMO number
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                {getSearchTypeIcon()}
              </div>
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Enter vessel name, company, or IMO..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="search-input-large pl-11"
              />
            </div>

            <Select value={searchType} onValueChange={setSearchType}>
              <SelectTrigger 
                data-testid="search-type-select"
                className="w-full sm:w-40 h-14 bg-white border-slate-200"
              >
                <SelectValue placeholder="Search by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Fields</SelectItem>
                <SelectItem value="vessel">Vessel Name</SelectItem>
                <SelectItem value="company">Company</SelectItem>
                <SelectItem value="imo">IMO Number</SelectItem>
              </SelectContent>
            </Select>

            <Button
              data-testid="search-button"
              onClick={handleSearch}
              disabled={isLoading}
              className="h-14 px-8 bg-ocean-900 hover:bg-ocean-800 text-white font-semibold rounded-lg btn-active"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Searching...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Search className="w-4 h-4" />
                  Search
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {hasSearched && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-card overflow-hidden">
          {results.length > 0 ? (
            <>
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="font-heading font-semibold text-lg text-ocean-900">
                  Search Results
                </h3>
                <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                  {results.length} found
                </Badge>
              </div>

              <div className="overflow-x-auto">
                <Table className="vessel-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                      <TableHead className="w-12 text-center">#</TableHead>
                      <TableHead>Vessel Name</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>IMO</TableHead>
                      <TableHead>Emails</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead className="text-center">Match</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map((result, index) => (
                      <TableRow
                        key={index}
                        data-testid={`result-row-${index}`}
                        className="result-row hover:bg-slate-50/80 border-b border-slate-50"
                      >
                        <TableCell className="text-center text-slate-400 font-mono text-xs">
                          {index + 1}
                        </TableCell>
                        <TableCell className="font-medium text-ocean-900">
                          {result.vessel_name || "-"}
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {result.company_name || "-"}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-slate-500">
                          {result.imo_number || "-"}
                        </TableCell>
                        <TableCell>
                          <EmailCell result={result} />
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {result.country || "-"}
                        </TableCell>
                        <TableCell className="text-center">
                          <span
                            className={`match-score ${getMatchScoreClass(
                              result.match_score
                            )}`}
                          >
                            {result.match_score}%
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <div className="empty-state py-16">
              <Anchor className="empty-state-icon" />
              <h3 className="font-heading font-semibold text-xl text-slate-700 mb-2">
                No vessels found
              </h3>
              <p className="text-slate-500 max-w-md mx-auto">
                Try adjusting your search query or changing the search type to find
                more results.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Initial State */}
      {!hasSearched && (
        <div className="empty-state py-16 bg-white border border-slate-200 rounded-xl shadow-card">
          <Search className="empty-state-icon" />
          <h3 className="font-heading font-semibold text-xl text-slate-700 mb-2">
            Start searching
          </h3>
          <p className="text-slate-500 max-w-md mx-auto">
            Enter a vessel name, company, or IMO number to find email addresses from
            the master database.
          </p>
        </div>
      )}
    </div>
  );
}
